import { getSupabase, getUserData, signOut } from './lib/supabase';
import { getCurrentLivestream, subscribeLivestreamUpdates, trackLivestreamView } from './lib/livestream';
import type { Livestream } from './lib/types';
import { hasCheckedInToday, createConversation } from './lib/chat';
import { DailyCheckinModal, injectDailyCheckinStyles } from './components/DailyCheckinModal';
import { ChatModal, injectChatModalStyles } from './components/ChatModal';

const loadingState = document.getElementById('loadingState')!;
const dashboardContent = document.getElementById('dashboardContent')!;
const displayNameEl = document.getElementById('displayName'); // Optional in RPG layout
const tierKicker = document.getElementById('tier-kicker'); // Optional in RPG layout
const tierLede = document.getElementById('tier-lede'); // Optional - removed in simplified dashboard
const brandText = document.getElementById('brand-text');
const signOutBtn = document.getElementById('signOutBtn')!

// Content containers (optional - some may be in menu now)
const friendsContent = document.getElementById('friends-content');
const companionContent = document.getElementById('companion-content');
const livestreamContainer = document.getElementById('livestreamContainer')!;

/**
 * Extract YouTube video ID from various URL formats
 */
function getYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\?\/]+)/,
    /youtube\.com\/live\/([^&\?\/]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match) return match[1];
  }

  return null;
}

/**
 * Format date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString);
  const options: Intl.DateTimeFormatOptions = {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  };
  return date.toLocaleDateString('en-US', options);
}

/**
 * Render livestream embed
 */
function renderLivestream(livestream: Livestream, containerEl: HTMLElement) {
  const videoId = getYouTubeId(livestream.youtube_url);
  const isLive = livestream.status === 'live';
  const isScheduled = livestream.status === 'scheduled';

  const badgeClass = isLive ? '' : 'scheduled';
  const badgeText = isLive ? '● Live Now' : 'Scheduled';

  const dateInfo = isLive
    ? `Started ${formatDate(livestream.started_at!)}`
    : isScheduled && livestream.scheduled_for
    ? `Scheduled for ${formatDate(livestream.scheduled_for)}`
    : '';

  containerEl.innerHTML = `
    <div class="livestream-card">
      <span class="livestream-badge ${badgeClass}">${badgeText}</span>
      <h3 class="livestream-title">${livestream.title}</h3>
      ${livestream.description ? `<p class="livestream-description">${livestream.description}</p>` : ''}
      ${dateInfo ? `<p class="livestream-date">${dateInfo}</p>` : ''}
      ${videoId ? `
        <div class="video-container">
          <iframe
            src="https://www.youtube.com/embed/${videoId}${isLive ? '?autoplay=0' : ''}"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      ` : ''}
    </div>
  `;

  // Track view
  if (isLive) {
    trackLivestreamView(livestream.id);
  }
}

/**
 * Render "no livestream" state
 */
function renderNoLivestream(containerEl: HTMLElement) {
  containerEl.innerHTML = `
    <div class="no-stream">
      <h3>No livestream right now</h3>
      <p>Check back soon, or browse the <a href="/archive.html" style="color: var(--accent-friends); text-decoration: underline;">archive</a> to watch past streams.</p>
    </div>
  `;
}

/**
 * Show Friends tier dashboard
 */
async function showFriendsDashboard(userData: any) {
  if (tierKicker) tierKicker.textContent = 'HEALING JOURNEY';
  if (tierLede) tierLede.textContent = 'Let\'s continue your healing practice';
  if (brandText) brandText.textContent = 'ARHB Dashboard';

  // Set CSS variable for accent color
  document.documentElement.style.setProperty('--accent', '#3DDAB4');
  document.documentElement.style.setProperty('--accent-dark', '#2cb895');
  document.documentElement.style.setProperty('--accent-ink', '#0b1f18');

  // Show Friends content (if elements exist - they may be in menu now)
  if (friendsContent) friendsContent.style.display = 'block';
  if (companionContent) companionContent.classList.add('hidden');

  // Get current or upcoming livestream
  const livestream = await getCurrentLivestream();

  if (livestream) {
    renderLivestream(livestream, livestreamContainer);
  } else {
    renderNoLivestream(livestreamContainer);
  }

  // Subscribe to realtime updates
  const unsubscribe = subscribeLivestreamUpdates((updatedLivestream) => {
    // Re-render if this is the current stream or becomes live
    if (updatedLivestream.status === 'live' || updatedLivestream.id === livestream?.id) {
      renderLivestream(updatedLivestream, livestreamContainer);
    }
  });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    unsubscribe();
  });
}

/**
 * Show Companion tier dashboard
 */
async function showCompanionDashboard(userData: any) {
  if (tierKicker) tierKicker.textContent = 'HEALING JOURNEY';
  if (tierLede) tierLede.textContent = 'Let\'s continue your healing practice';
  if (brandText) brandText.textContent = 'ARHB Dashboard';

  // Set CSS variable for accent color
  document.documentElement.style.setProperty('--accent', '#3DDAB4');
  document.documentElement.style.setProperty('--accent-dark', '#2cb895');
  document.documentElement.style.setProperty('--accent-ink', '#0b1f18');

  // Show both Friends and Companion content (if elements exist)
  if (friendsContent) friendsContent.style.display = 'block';
  if (companionContent) companionContent.classList.remove('hidden');

  // Populate companion info
  const statusEl = document.getElementById('companion-status');
  if (statusEl) {
    // Check for companion signup data
    const supabase = getSupabase();
    const { data: companionSignup } = await supabase
      .from('companion_signups')
      .select('*')
      .eq('email', userData.user.email)
      .single();

    if (!companionSignup) {
      statusEl.innerHTML = `
        <p>Complete your onboarding to get matched with a companion group.</p>
        <a href="/companion-beta/onboarding.html" class="btn" style="margin-top: 12px;">Complete Onboarding</a>
      `;
    } else {
      statusEl.textContent = 'Your onboarding is complete. We\'ll notify you when a matching group is ready.';
    }
  }

  // Get current or upcoming livestream
  const livestream = await getCurrentLivestream();

  if (livestream) {
    renderLivestream(livestream, livestreamContainer);
  } else {
    renderNoLivestream(livestreamContainer);
  }

  // Subscribe to realtime updates
  const unsubscribe = subscribeLivestreamUpdates((updatedLivestream) => {
    if (updatedLivestream.status === 'live' || updatedLivestream.id === livestream?.id) {
      renderLivestream(updatedLivestream, livestreamContainer);
    }
  });

  window.addEventListener('beforeunload', () => {
    unsubscribe();
  });
}

/**
 * Show Mentor tier dashboard
 */
async function showMentorDashboard(userData: any) {
  if (tierKicker) tierKicker.textContent = 'HEALING JOURNEY';
  if (tierLede) tierLede.textContent = 'Let\'s continue your healing practice';
  if (brandText) brandText.textContent = 'ARHB Dashboard';

  // Set CSS variable for accent color
  document.documentElement.style.setProperty('--accent', '#9B59B6');
  document.documentElement.style.setProperty('--accent-dark', '#8e44ad');
  document.documentElement.style.setProperty('--accent-ink', '#1a0a1f');

  // For now, show companion dashboard (mentor gets all features)
  await showCompanionDashboard(userData);
  if (tierKicker) tierKicker.textContent = 'HEALING JOURNEY';
  if (brandText) brandText.textContent = 'ARHB Dashboard';
}

/**
 * Poll for active membership with retry logic
 * Used after Stripe checkout to wait for webhook processing
 */
async function waitForActiveMembership(maxAttempts = 10, delayMs = 2000): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const membership = await getUserData();
    if (membership && membership.membership && membership.membership.status === 'active') {
      return true;
    }

    // Wait before next attempt
    if (attempt < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
  }

  return false;
}

/**
 * Initialize dashboard
 */
async function init() {
  try {
    // Check if returning from Stripe checkout
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');

    if (sessionId) {
      // User just completed payment, wait for webhook to process
      loadingState.innerHTML = '<p>Processing your payment...</p>';

      const hasActiveMembership = await waitForActiveMembership();

      if (!hasActiveMembership) {
        alert('Payment processing is taking longer than expected. Please refresh the page in a moment.');
        window.location.href = '/';
        return;
      }

      // Remove session_id from URL without reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Get user data
    const userData = await getUserData();

    if (!userData || !userData.user) {
      // Not logged in
      window.location.href = '/';
      return;
    }

    if (!userData.membership || userData.membership.status !== 'active') {
      // No active membership
      alert('You don\'t have an active membership. Please subscribe to continue.');
      window.location.href = '/';
      return;
    }

    // Display user name (if element exists in layout)
    if (displayNameEl) {
      displayNameEl.textContent = userData.profile?.display_name || 'there';
    }

    // Show tier-specific dashboard
    const tier = userData.membership.tier;

    switch (tier) {
      case 'FRIENDS':
        await showFriendsDashboard(userData);
        break;
      case 'COMPANION':
        await showCompanionDashboard(userData);
        break;
      case 'MENTOR':
        await showMentorDashboard(userData);
        break;
      default:
        throw new Error(`Unknown tier: ${tier}`);
    }

    // Hide loading, show content
    loadingState.classList.add('hidden');
    dashboardContent.classList.remove('hidden');

    // Check if user needs to do daily check-in
    const hasCheckedIn = await hasCheckedInToday(userData.user.id);
    if (!hasCheckedIn) {
      // Inject styles and show check-in modal
      injectDailyCheckinStyles();
      const checkinModal = new DailyCheckinModal(userData.user.id);

      // Show modal after a brief delay to let dashboard settle
      setTimeout(() => {
        checkinModal.open();
      }, 500);
    }

    // Set up chat modals and button handlers
    setupChatButtons(userData.user.id);

    // Set up fun button
    setupFunButton();

  } catch (error) {
    console.error('Dashboard init error:', error);
    loadingState.innerHTML = '<p style="color: #dc3545;">Failed to load dashboard. Please try refreshing the page.</p>';
  }
}

/**
 * Set up chat buttons and modals
 */
function setupChatButtons(userId: string) {
  // Inject chat modal styles
  injectChatModalStyles();

  // Create global chat modal instance
  const chatModal = new ChatModal(userId);

  // Speech bubble click to show inline response
  const speechBubble = document.getElementById('speech-bubble');
  const featuredResponseArea = document.getElementById('featured-response-area');
  const featuredInput = document.getElementById('featured-response-input') as HTMLTextAreaElement;
  const featuredSendBtn = document.getElementById('featured-send-btn');
  const viewChatBtn = document.getElementById('view-chat-btn-top');

  if (speechBubble && featuredResponseArea) {
    speechBubble.addEventListener('click', () => {
      if (featuredResponseArea.style.display === 'none') {
        featuredResponseArea.style.display = 'block';
        featuredInput?.focus();
      }
    });
  }

  // Featured send button - send message inline
  if (featuredSendBtn && featuredInput) {
    featuredSendBtn.addEventListener('click', async () => {
      const message = featuredInput.value.trim();
      if (!message) return;

      // Get or create featured conversation
      let conversationId = localStorage.getItem('featured_conversation_id');

      if (!conversationId) {
        const conversation = await createConversation(userId, 'featured', {
          title: 'Featured Message Discussion',
        });

        if (conversation) {
          conversationId = conversation.id;
          localStorage.setItem('featured_conversation_id', conversationId);
        } else {
          alert('Failed to create conversation. Please try again.');
          return;
        }
      }

      // Send message via AI
      featuredSendBtn.disabled = true;
      featuredInput.disabled = true;

      await callAI(conversationId, message);

      // Clear input and redirect to chats page
      featuredInput.value = '';
      featuredInput.disabled = false;
      featuredSendBtn.disabled = false;

      window.location.href = `/friends/chats.html?conversation_id=${conversationId}`;
    });
  }

  // View chat button - navigate to chats page
  if (viewChatBtn) {
    viewChatBtn.addEventListener('click', async () => {
      // Get or create featured conversation
      let conversationId = localStorage.getItem('featured_conversation_id');

      if (!conversationId) {
        const conversation = await createConversation(userId, 'featured', {
          title: 'Featured Message Discussion',
        });

        if (conversation) {
          conversationId = conversation.id;
          localStorage.setItem('featured_conversation_id', conversationId);
        }
      }

      if (conversationId) {
        window.location.href = `/friends/chats.html?conversation_id=${conversationId}`;
      } else {
        window.location.href = '/friends/chats.html';
      }
    });
  }

  // Floating anxiety help button
  const floatingAnxietyBtn = document.getElementById('floating-anxiety-btn');
  if (floatingAnxietyBtn) {
    floatingAnxietyBtn.addEventListener('click', async () => {
      // Create a new urgent help conversation
      const conversation = await createConversation(userId, 'general', {
        title: 'Anxiety Help',
        metadata: { urgent: true },
      });

      if (conversation) {
        chatModal.open(conversation.id, 'Anxiety Help');

        // Pre-fill with urgent message
        setTimeout(() => {
          const chatInput = document.getElementById('chat-modal-input') as HTMLTextAreaElement;
          const sendBtn = document.getElementById('chat-modal-send') as HTMLButtonElement;
          if (chatInput && sendBtn) {
            chatInput.value = "I'm feeling really anxious right now and need some help calming down.";
            chatInput.focus();
          }
        }, 300);
      } else {
        alert('Failed to create conversation. Please try again.');
      }
    });
  }
}

/**
 * Set up fun button interaction
 */
function setupFunButton() {
  const funBtn = document.getElementById('fun-button');
  const bunnyCharacter = document.getElementById('bunny-character');

  if (!funBtn || !bunnyCharacter) return;

  funBtn.addEventListener('click', () => {
    // Add character bounce animation
    bunnyCharacter.style.animation = 'bunny-bounce 0.6s ease 3';
    setTimeout(() => {
      bunnyCharacter.style.animation = '';
    }, 1800);

    // Show coming soon toast
    const toast = document.createElement('div');
    toast.style.cssText = `
      position: fixed;
      bottom: 24px;
      left: 50%;
      transform: translateX(-50%) translateY(100px);
      background: #fff;
      color: var(--text-color);
      padding: 16px 32px;
      border: 2px solid #000;
      border-radius: 999px;
      box-shadow: 0 4px 0 #000;
      font-weight: 700;
      font-size: 16px;
      z-index: 9999;
      transition: transform 0.3s ease;
    `;
    toast.textContent = '🎮 Adventure mode coming soon!';

    document.body.appendChild(toast);

    // Slide in
    setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(0)';
    }, 10);

    // Slide out and remove
    setTimeout(() => {
      toast.style.transform = 'translateX(-50%) translateY(100px)';
      setTimeout(() => toast.remove(), 300);
    }, 2500);
  });
}

// Sign out handler
signOutBtn.addEventListener('click', async (e) => {
  e.preventDefault();
  await signOut();
});

// Initialize on load
init();
